import { apiRequest } from '../../shared/api/client.ts';
import type {
  AdminUser,
  AdminUserListResponse,
  AdminUserOverview,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  ResetAdminUserPasswordRequest,
  AdminUserSpacesResponse,
  SetAdminUserSpacesRequest,
  PlatformSettingsResponse,
  UpdatePlatformSettingsRequest,
  ActivityLogQuery,
  ActivityLogResponse,
  KeycloakPreviewRequest,
  KeycloakPreviewResponse,
  KeycloakImportRequest,
  KeycloakImportResponse,
} from '@lens/shared';

// ─── Users ──────────────────────────────────────────────────────────────

export async function listAdminUsers(): Promise<AdminUserListResponse> {
  return apiRequest('/api/admin/users');
}

export async function getAdminUserOverview(id: string): Promise<AdminUserOverview> {
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}/overview`);
}

export async function createAdminUser(data: CreateAdminUserRequest): Promise<AdminUser> {
  return apiRequest('/api/admin/users', { method: 'POST', body: data });
}

export async function updateAdminUser(
  id: string,
  data: UpdateAdminUserRequest,
): Promise<AdminUser> {
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function resetAdminUserPassword(
  id: string,
  data: ResetAdminUserPasswordRequest,
): Promise<void> {
  await apiRequest(`/api/admin/users/${encodeURIComponent(id)}/password`, {
    method: 'POST',
    body: data,
  });
}

export async function getAdminUserSpaces(id: string): Promise<AdminUserSpacesResponse> {
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}/spaces`);
}

export async function setAdminUserSpaces(
  id: string,
  data: SetAdminUserSpacesRequest,
): Promise<AdminUserSpacesResponse> {
  return apiRequest(`/api/admin/users/${encodeURIComponent(id)}/spaces`, {
    method: 'PUT',
    body: data,
  });
}

// ─── Platform settings ──────────────────────────────────────────────────

export async function getPlatformSettings(): Promise<PlatformSettingsResponse> {
  return apiRequest('/api/admin/settings');
}

export async function updatePlatformSettings(
  data: UpdatePlatformSettingsRequest,
): Promise<PlatformSettingsResponse> {
  return apiRequest('/api/admin/settings', {
    method: 'PATCH',
    body: data,
  });
}

// ─── Загрузка пользователей из Keycloak ────────────────────────────────

export async function previewKeycloakUsers(
  params: KeycloakPreviewRequest,
): Promise<KeycloakPreviewResponse> {
  return apiRequest('/api/admin/sso/keycloak/preview', { method: 'POST', body: params });
}

export async function importKeycloakUsers(
  params: KeycloakImportRequest,
): Promise<KeycloakImportResponse> {
  return apiRequest('/api/admin/sso/keycloak/import', { method: 'POST', body: params });
}

// ─── LLM models ────────────────────────────────────────────────────────

export async function fetchLlmModels(opts?: {
  baseUrl?: string;
}): Promise<string[]> {
  // Ключ не передаём — бэкенд берёт персональный ключ текущего админа.
  const res = await apiRequest<{ models: string[] }>('/api/admin/llm-models', {
    method: 'POST',
    body: { baseUrl: opts?.baseUrl },
  });
  return res.models;
}

export async function pingLlmAdmin(): Promise<{ ok: boolean; model?: string; previewText?: string }> {
  return apiRequest('/api/ai/ping', { method: 'POST' });
}

// ─── Activity log ───────────────────────────────────────────────────────

export async function listActivity(
  params: Partial<ActivityLogQuery> = {},
): Promise<ActivityLogResponse> {
  const qs = new URLSearchParams();
  if (params.source) qs.set('source', params.source);
  if (params.userId) qs.set('userId', params.userId);
  if (params.spaceId) qs.set('spaceId', params.spaceId);
  if (params.action) qs.set('action', params.action);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.before) qs.set('before', params.before);
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiRequest(`/api/admin/activity${query ? `?${query}` : ''}`);
}
